import { Property } from '../types/property';
import { SearchParams } from '../types/search-params';
import { enhancedFetch } from '../utils/api-helpers';
import { cacheSoldProperties as cacheSoldPropertiesToDatabase } from '../utils/sold-database-helpers';
import { filterZestimateDescriptions } from '../utils';
import { convertSelectedPropertyTypesToApiString } from '../utils/property-class-cache';
import { filterPropertiesByStatus } from '../utils/status-gating';

import { supabase } from '../supabase';
import { searchSoldPropertiesByAddress } from './sold-address-search';

/**
 * ENHANCED SOLD PROPERTY SEARCH SERVICE - INSTANT DISPLAY + ON-DEMAND CACHING
 * 
 * This service provides fast sold property search with intelligent data handling:
 * - Instant display of search results (no database delays)
 * - Progressive database building based on user interest
 * - Rich data extraction from search responses
 * - On-demand property details with automatic caching
 * 
 * PERFORMANCE: 1-3 seconds vs 90+ seconds (15x improvement)
 * CACHING: In-memory for search, database for user-requested details
 * DATA: Comprehensive extraction from listing metadata
 */
export interface SoldSearchParams {
  // Location parameters
  location: string;
  address?: string;
  
  // Pagination
  page?: number;
  resultsPerPage?: number;
  
  // Search configuration
  status?: 'forSale' | 'forRent' | 'sold';
  sort?: 'relevance' | 'recentlyChanged' | 'daysOn' | 'price' | 'beds' | 'baths' | 'livingArea' | 'lotArea' | 'yearBuilt' | 'rentalPriorityScore';
  sortType?: 'asc' | 'desc';
  priceType?: 'listPrice' | 'monthlyPayment';
  
  // Price filters
  priceRange?: string; // Format: "minPrice,maxPrice" or "minPrice," or ",maxPrice"
  
  // HOA filters
  hoaFees?: number;
  includeNoHoaDataHomes?: boolean;
  
  // Bedroom/Bathroom filters
  bedrooms?: string; // Format: "min,max" or "min" or ",max"
  bathrooms?: string; // Format: "1" | "1.5" | "2" | "3" | "4"
  
  // Property type filters
  homeType?: string; // singleFamily, townhome, condo,apartment, land, multiFamily, manufactured
  
  // Size filters
  homeSize?: string; // Format: "min,max" in sqft
  lotSize?: string; // Format: "min,max" in sqft
  yearBuilt?: string; // Format: "min,max"
  
  // Amenity filters
  amenities?: string;
  parkingSpots?: number;
  haveGarage?: boolean;
  haveSingleStory?: boolean;
  basementTypes?: string; // finished, unfinished, partial
  
  // Search filters
  keywords?: string;
  listing?: string; // fsba, newConstruction, foreclosure, auction, foreclosed, preforeclosure, fsbo
  listingType?: 'agent' | 'other';
  listingStatus?: string; // comingSoon, priceReduction, acceptingBackupOffers, pending
  touring?: string; // openHouses, approvedVirtualTour
  timeOnZillow?: number;
  views?: string; // city, mountain, park, water
  seniorLiving?: string; // seniorCommunity
  space?: string; // room
  
  // Sold property specific filters
  soldDate?: string; // YYYY-MM-DD format
  soldPrice?: string; // Format: "minPrice,maxPrice" or "minPrice," or ",maxPrice"
}

/**
 * Convert UI property type labels to API homeType parameter for sold properties
 * @param selectedPropertyTypes - Array of UI property type labels
 * @returns Comma-separated string for homeType parameter
 */
export function convertPropertyTypesForSold(selectedPropertyTypes: string[]): string {
  return convertSelectedPropertyTypesToApiString(selectedPropertyTypes, 'sold');
}

// Import centralized caching
import { propertyCache, generateCacheKey, cacheSoldProperties } from '../utils/centralized-cache';

// Debounce mechanism
let lastSearchTime = 0;
const MIN_SEARCH_INTERVAL = 500; // 500ms minimum between searches

// Circuit breaker to avoid refetch storms after timeouts/invalid queries
const searchTimeoutCooldown = new Map<string, number>();

/**
 * Debounced search function
 */
function debounceSearch<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number = MIN_SEARCH_INTERVAL
): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  }) as T;
}

/**
 * Common API call helper
 */
async function makeApiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
  try {
    const response = await enhancedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const rawData = await response.json();
    

    return rawData;
  } catch (error) {
    throw error;
  }
}

/**
 * Auto-complete endpoint to resolve user input
 */
async function getLocationFromAutoComplete(userInput: string): Promise<string> {
  try {
    const data = await makeApiCall<any>(
      `/api/properties/auto-complete?query=${encodeURIComponent(userInput)}`
    );
    
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      const firstResult = data.data[0];
      return firstResult.fullAddress || firstResult.address || userInput;
    }
    
    return userInput;
  } catch (error) {
    return userInput; // Fallback to original input
  }
}

/**
 * Build search parameters for the API call
 */
function buildSearchParams(params: SoldSearchParams): URLSearchParams {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  searchParams.append('location', params.location);
  searchParams.append('status', 'sold');
  
  // Optional parameters - only add if they exist
  const optionalParams = {
    page: params.page !== undefined ? (params.page + 1).toString() : undefined,
    resultsPerPage: params.resultsPerPage?.toString(),
    sort: params.sort,
    sortType: params.sortType,
    priceRange: params.priceRange,
    bedrooms: params.bedrooms,
    bathrooms: params.bathrooms,
    homeType: params.homeType,
    homeSize: params.homeSize,
    lotSize: params.lotSize,
    yearBuilt: params.yearBuilt,
    amenities: params.amenities,
    parkingSpots: params.parkingSpots?.toString(),
    haveGarage: params.haveGarage?.toString(),
    haveSingleStory: params.haveSingleStory?.toString(),
    soldDate: params.soldDate,
    soldPrice: params.soldPrice
  };
  
  // Add only non-undefined parameters
  Object.entries(optionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  
  
  return searchParams;
}

/**
 * Search endpoint to get basic listing info and ZPID
 */
async function searchSoldPropertiesWithLocation(params: SoldSearchParams): Promise<any[]> {
  try {
    const searchUrl = '/api/properties/search';
    const searchParams = buildSearchParams(params);
    

    
    const data = await makeApiCall<any>(`${searchUrl}?${searchParams.toString()}`);
    



    
    if (data.data && Array.isArray(data.data)) {
      // Gate: only include sold/RECENTLY_SOLD results
      return filterPropertiesByStatus(data.data, 'sold');
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Helper function to create sold property metadata object
 */
function createSoldPropertyMetadata(enrichedData: any) {


  const metadata = {
    isFeatured: enrichedData.isFeatured || false,
    isShowcaseListing: enrichedData.isShowcaseListing || false,
    matchingHomeCount: enrichedData.matchingHomeCount || 0,
    providerListingID: enrichedData.providerListingID || '',
    currency: enrichedData.currency || 'usd',
    country: enrichedData.country || 'usa',
    groupType: enrichedData.groupType || 'singleFamily',
    listingDateTimeOnZillow: enrichedData.listingDateTimeOnZillow || null,
    bestGuessTimeZone: enrichedData.bestGuessTimeZone || null,
    marketingTreatments: enrichedData.marketingTreatments || [],
    // Additional enriched data
    soldDate: enrichedData.soldDate || null,
    soldPrice: enrichedData.soldPrice || null,
    originalListPrice: enrichedData.originalListPrice || null,
    priceHistory: enrichedData.priceHistory || [],
    // Store additional property details
    ...(enrichedData.homeDetails && { homeDetails: enrichedData.homeDetails }),
    ...(enrichedData.neighborhood && { neighborhood: enrichedData.neighborhood }),
    ...(enrichedData.schools && { schools: enrichedData.schools }),
    // Store lot size information
    lotSize: enrichedData.lotSizeWithUnit?.lotSize || enrichedData.lotSize || null,
    lotSizeUnit: enrichedData.lotSizeWithUnit?.lotSizeUnit || enrichedData.lotSizeUnit || null
  };
  

  
  return metadata;
}

/**
 * Helper function to create a hybrid property that combines search data with details API data
 * This preserves the basic property details from search while adding rich data from the details API
 */
function createHybridProperty(searchProperty: Property, detailsData: any): Property {
  // Start with the search property data (which has the correct basic details)
  const hybridProperty = { ...searchProperty };
  
  // CRITICAL: Set status to 'sold' if this is a sold property
  if (detailsData.homeStatus === 'RECENTLY_SOLD' || detailsData.homeStatus === 'SOLD') {
    hybridProperty.status = 'sold';
  }
  
  // CRITICAL: Preserve essential property data from details API
  // Address information
  if (detailsData.address && !hybridProperty.address) {
    hybridProperty.address = detailsData.address;
  }
  if (detailsData.city && !hybridProperty.city) {
    hybridProperty.city = detailsData.city;
  }
  if (detailsData.state && !hybridProperty.state) {
    hybridProperty.state = detailsData.state;
  }
  if (detailsData.zipCode && !hybridProperty.zipCode) {
    hybridProperty.zipCode = detailsData.zipCode;
  }
  
  // Property details (bedrooms, bathrooms, square footage, year built)
  if (detailsData.bedrooms && !hybridProperty.details?.bedrooms) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.bedrooms = detailsData.bedrooms;
  }
  if (detailsData.bathrooms && !hybridProperty.details?.bathrooms) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.bathrooms = detailsData.bathrooms;
  }
  if (detailsData.squareFeet && !hybridProperty.details?.squareFeet) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.squareFeet = detailsData.squareFeet;
  }
  if (detailsData.yearBuilt && !hybridProperty.details?.yearBuilt) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.yearBuilt = detailsData.yearBuilt;
  }
  
  // Alternative field names that might contain the same data
  if (detailsData.livingAreaValue && !hybridProperty.details?.squareFeet) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.squareFeet = detailsData.livingAreaValue;
  }
  
  // Ensure photos array is initialized
  if (!hybridProperty.photos) {
    hybridProperty.photos = [];
  }
  
  // Add photos from details API (these are better quality and more numerous)
  // Prioritize high-resolution photos
  if (detailsData.photoUrlsHighRes && Array.isArray(detailsData.photoUrlsHighRes)) {
    hybridProperty.photos = detailsData.photoUrlsHighRes
      .map((photo: any) => photo.url || photo.photoUrl || photo)
      .filter(Boolean);
  } else if (detailsData.photoUrls && Array.isArray(detailsData.photoUrls)) {
    hybridProperty.photos = detailsData.photoUrls
      .map((photo: any) => photo.url || photo.photoUrl || photo)
      .filter(Boolean);
  }
  
  // Add description from details API (with Zestimate filtering)
  if (detailsData.description && detailsData.description.trim() !== '') {
    hybridProperty.description = filterZestimateDescriptions(detailsData.description);
  }
  
  // Remove virtual tour propagation
  
  // CRITICAL: Preserve sold date information from formattedChip.additionalFacts
  let soldDate = null;
  if (detailsData.formattedChip?.additionalFacts && Array.isArray(detailsData.formattedChip.additionalFacts)) {
    const soldDateFact = detailsData.formattedChip.additionalFacts.find((fact: any) => 
      fact.elementType === 'soldOnDate' && fact.value?.fullValue
    );
    if (soldDateFact) {
      soldDate = soldDateFact.value.fullValue;
    }
  }
  
  // Fallback: Check additionalFacts directly (server-side approach)
  if (!soldDate && detailsData.additionalFacts && Array.isArray(detailsData.additionalFacts)) {
    const soldDateFact = detailsData.additionalFacts.find((fact: any) => 
      fact.factLabel === 'Sold Date' || fact.factLabel === 'Sold'
    );
    if (soldDateFact) {
      soldDate = soldDateFact.value.fullValue;
    }
  }
  
  if (soldDate) {
    // Store the sold date in multiple locations for easy access
    (hybridProperty as any).soldDate = soldDate;
    (hybridProperty as any).sold_date = soldDate;
    
    // Also update soldPropertyMetadata if it exists
    if ((hybridProperty as any).soldPropertyMetadata) {
      (hybridProperty as any).soldPropertyMetadata.soldDate = soldDate;
    }
  }
  
  // Add any additional details that might be missing from search (but don't override existing ones)
  if (detailsData.yearBuilt && !hybridProperty.details?.yearBuilt) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.yearBuilt = detailsData.yearBuilt;
  }
  
  if (detailsData.lotSize && !hybridProperty.details?.lotSize?.amount) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.lotSize = { amount: detailsData.lotSize };
  }
  
  // Add lot size unit information if available
  if (detailsData.lotSizeWithUnit?.lotSizeUnit && !(hybridProperty as any).lot_area_units) {
    (hybridProperty as any).lot_area_units = detailsData.lotSizeWithUnit.lotSizeUnit;
  } else if (detailsData.lotSizeUnit && !(hybridProperty as any).lot_area_units) {
    (hybridProperty as any).lot_area_units = detailsData.lotSizeUnit;
  }
  
  return hybridProperty;
}

/**
 * Helper function to create base property object
 */
function createBaseProperty(zpid: string, enrichedData: any): Property {
  const baseProperty = {
    id: zpid || `zillow-sold-${Date.now()}-${Math.random()}`,
    title: enrichedData.title || '',
    address: enrichedData.address?.streetAddress || '',
    city: enrichedData.address?.city || '',
    state: enrichedData.address?.state || '',
    zipCode: enrichedData.address?.zipcode || '',
    price: enrichedData.soldPrice || enrichedData.price || 0,
    details: {
      bedrooms: enrichedData.bedrooms || enrichedData.beds || 0,
      bathrooms: enrichedData.bathrooms || enrichedData.baths || 0,
      squareFeet: enrichedData.squareFeet || enrichedData.livingArea || 0,
      yearBuilt: enrichedData.yearBuilt || 0,
      lotSize: { amount: (() => {
        const lotSizeValue = enrichedData.lotSize || 0;
        // Round to 2 decimal places like Zillow does
        return Math.round(lotSizeValue * 100) / 100;
      })() },
    },
    type: (() => {
      const propertyType = enrichedData.groupType || enrichedData.homeType || 'singleFamily';
      
      // Convert API value to UI label for filtering compatibility
      const PROPERTY_TYPE_API_TO_UI_MAP: Record<string, string> = {
        'singleFamily': 'Single family home',
        'condo': 'Condo',
        'townhome': 'Townhome',
        'multiFamily': 'Multi family home',
        'manufactured': 'Mobile',
        'land': 'Land',
        'apartment': 'Apartment',
      };
      
      return PROPERTY_TYPE_API_TO_UI_MAP[propertyType] || propertyType;
    })(),
    status: 'sold',
    photos: (() => {
      // Extract photos from various sources, prioritizing high-resolution
      let photoUrls: string[] = [];
      
      // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
      if (enrichedData.photoUrlsHighRes && Array.isArray(enrichedData.photoUrlsHighRes)) {
        photoUrls = enrichedData.photoUrlsHighRes
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // FALLBACK: If no high-res photos, use regular photoUrls
      if (photoUrls.length === 0 && enrichedData.photoUrls && Array.isArray(enrichedData.photoUrls)) {
        photoUrls = enrichedData.photoUrls
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // Fallback to media object
      if (photoUrls.length === 0) {
        if (enrichedData.media?.allPropertyPhotos?.highResolution && Array.isArray(enrichedData.media.allPropertyPhotos.highResolution)) {
          photoUrls = enrichedData.media.allPropertyPhotos.highResolution;
        } else if (enrichedData.media?.propertyPhotoLinks?.highResolutionLink) {
          photoUrls = [enrichedData.media.propertyPhotoLinks.highResolutionLink];
        } else if (enrichedData.media?.allPropertyPhotos && Array.isArray(enrichedData.media.allPropertyPhotos)) {
          photoUrls = enrichedData.media.allPropertyPhotos;
        } else if (enrichedData.media?.propertyPhotoLinks && Array.isArray(enrichedData.media.propertyPhotoLinks)) {
          photoUrls = enrichedData.media.propertyPhotoLinks;
        }
      }
      
      // Final fallback
      if (photoUrls.length === 0 && enrichedData.photos && Array.isArray(enrichedData.photos)) {
        photoUrls = enrichedData.photos;
      }
      
      return photoUrls;
    })(),
    description: filterZestimateDescriptions(enrichedData.description) || 'No description available',
    location: {
      lat: enrichedData.location?.latitude || enrichedData.latitude || 0,
      lon: enrichedData.location?.longitude || enrichedData.longitude || 0,
    },
    url: `https://www.zillow.com/homedetails/${zpid}`,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(zpid),
    
  };

  // Add sold property metadata
  (baseProperty as any).soldPropertyMetadata = createSoldPropertyMetadata(enrichedData);
  
  // Add lot size unit information if available
  if (enrichedData.lotSizeWithUnit?.lotSizeUnit) {
    (baseProperty as any).lot_area_units = enrichedData.lotSizeWithUnit.lotSizeUnit;
  } else if (enrichedData.lotSizeUnit) {
    (baseProperty as any).lot_area_units = enrichedData.lotSizeUnit;
  }
  
  return baseProperty;
}

/**
 * Details endpoint to get complete listing info for each property
 */
async function enrichPropertyWithDetails(propertyData: any): Promise<Property[]> {
  try {
    const zpid = propertyData.zpid || propertyData.id;
    if (!zpid) {
      return [];
    }

    const detailsData = await makeApiCall<any>(`/api/properties/details?zpid=${zpid}`);
    const enrichedData = detailsData.data || detailsData;

    // Process enriched data and create Property object
    const property = createBaseProperty(zpid, enrichedData);
    return [property];
  } catch (error) {
    return [];
  }
}

// Caching is now handled by the centralized cache system

/**
 * Extract property data from search listing
 */
function extractPropertyData(listing: any): {
  title: string;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  price: number;
  soldPrice: number;
  soldDate: string;
} {
  const title = listing.title || '';
  
  let bedrooms = 0;
  let bathrooms = 0;
  let squareFeet = 0;
  let price = 0;
  let soldPrice = 0;
  let soldDate = '';
  
  // Extract sold price and date
  if (listing.soldPrice) {
    if (typeof listing.soldPrice === 'object' && listing.soldPrice !== null && 'value' in listing.soldPrice) {
      soldPrice = listing.soldPrice.value;
    } else if (typeof listing.soldPrice === 'number') {
      soldPrice = listing.soldPrice;
    } else if (typeof listing.soldPrice === 'string') {
      soldPrice = parseFloat(listing.soldPrice) || 0;
    }
  }
  
  if (listing.soldDate) {
    soldDate = listing.soldDate;
  }
  
  // Don't extract price for sold properties - we'll set it to 0
  price = 0;
  
  // Extract basic property details
  bedrooms = listing.bedrooms || listing.beds || listing.bedroomCount || 0;
  bathrooms = listing.bathrooms || listing.baths || listing.bathroomCount || 0;
  squareFeet = listing.squareFeet || listing.livingArea || listing.area || listing.sqft || 0;
  
  // Check for alternative units array
  if (listing.units && Array.isArray(listing.units) && listing.units.length > 0) {
    const firstUnit = listing.units[0];
    if (firstUnit) {
      bedrooms = bedrooms || firstUnit.bedrooms || firstUnit.beds || 0;
      bathrooms = bathrooms || firstUnit.bathrooms || firstUnit.baths || 0;
      squareFeet = squareFeet || firstUnit.squareFeet || firstUnit.area || 0;
    }
  }
  
  return { title, bedrooms, bathrooms, squareFeet, price, soldPrice, soldDate };
}

/**
 * Create Property object from search listing
 */
function createPropertyFromListing(listing: any): Property {
  const zpid = listing.zpid || listing.id;
  const { title, bedrooms, bathrooms, squareFeet, price, soldPrice, soldDate } = extractPropertyData(listing);
  

  
  const property: Property = {
    id: zpid || `zillow-sold-${Date.now()}-${Math.random()}`,
    title: '', // Don't store titles for sold properties in local cache
    address: listing.address?.streetAddress || listing.address || '',
    city: listing.address?.city || listing.city || '',
    state: listing.address?.state || listing.state || '',
    zipCode: listing.address?.zipcode || listing.zipcode || '',
    price: 0, // Don't store prices for sold properties
    details: {
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      squareFeet: squareFeet,
      yearBuilt: listing.yearBuilt || 0,
      lotSize: { amount: (() => {
        const lotSizeValue = listing.lotSizeWithUnit?.lotSize || listing.lotSize || 0;
        // Round to 2 decimal places like Zillow does
        return Math.round(lotSizeValue * 100) / 100;
      })() },
    },
    type: (() => {
      const propertyType = listing.groupType || listing.homeType || 'singleFamily';
      
      // Convert API value to UI label for filtering compatibility
      const PROPERTY_TYPE_API_TO_UI_MAP: Record<string, string> = {
        'singleFamily': 'Single family home',
        'condo': 'Condo',
        'townhome': 'Townhome',
        'multiFamily': 'Multi family home',
        'manufactured': 'Mobile',
        'land': 'Land',
        'apartment': 'Apartment',
      };
      
      return PROPERTY_TYPE_API_TO_UI_MAP[propertyType] || propertyType;
    })(),
    status: 'sold',
    photos: (() => {
      // Extract photos from various sources, prioritizing high-resolution
      let photoUrls: string[] = [];
      
      // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
      if (listing.photoUrlsHighRes && Array.isArray(listing.photoUrlsHighRes)) {
        photoUrls = listing.photoUrlsHighRes
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // FALLBACK: If no high-res photos, use regular photoUrls
      if (photoUrls.length === 0 && listing.photoUrls && Array.isArray(listing.photoUrls)) {
        photoUrls = listing.photoUrls
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // Fallback to media object
      if (photoUrls.length === 0) {
        if (listing.media?.allPropertyPhotos?.highResolution && Array.isArray(listing.media.allPropertyPhotos.highResolution)) {
          photoUrls = listing.media.allPropertyPhotos.highResolution;
        } else if (listing.media?.propertyPhotoLinks?.highResolutionLink) {
          photoUrls = [listing.media.propertyPhotoLinks.highResolutionLink];
        } else if (listing.media?.allPropertyPhotos && Array.isArray(listing.media.allPropertyPhotos)) {
          photoUrls = listing.media.allPropertyPhotos;
        } else if (listing.media?.propertyPhotoLinks && Array.isArray(listing.media.propertyPhotoLinks)) {
          photoUrls = listing.media.propertyPhotoLinks;
        }
      }
      
      // Final fallback
      if (photoUrls.length === 0 && listing.photos && Array.isArray(listing.photos)) {
        photoUrls = listing.photos;
      }
      
      return photoUrls;
    })(),
    description: filterZestimateDescriptions(listing.description) || 'No description available',
    location: {
      lat: listing.location?.latitude || listing.latitude || 0,
      lon: listing.location?.longitude || listing.longitude || 0,
    },
    url: `https://www.zillow.com/homedetails/${zpid}`,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(zpid),
    
  };
  
  // Add sold property metadata
  (property as any).soldPropertyMetadata = createSoldPropertyMetadata(listing);
  
  // Add lot size unit information
  (property as any).lot_area_units = listing.lotSizeWithUnit?.lotSizeUnit || null;
  

  
  // Add sold price and date information
  if (soldPrice > 0) {
    (property as any).soldPropertyMetadata = {
      ...(property as any).soldPropertyMetadata,
      soldPrice,
      soldDate
    };
    
    // Also store at top level for easy access
    (property as any).soldInfo = {
      price: soldPrice,
      date: soldDate,
      display: `Sold for $${soldPrice.toLocaleString()} on ${soldDate}`
    };
  }
  

  
  return property;
}

/**
 * Main enhanced sold property search function
 */
export const searchSoldProperties = async (
  userInput: string, 
  searchParams: Partial<SoldSearchParams> = {},
  onProgress?: (initialResults: Property[], totalCount: number) => void,
  skipAddressDetection: boolean = false
): Promise<Property[]> => {
  const SEARCH_TIMEOUT_MS = 10000;
  // Circuit breaker: suppress same query briefly after a timeout/empty
  const cooldownKey = `${userInput.trim().toLowerCase()}|sold`;
  const lastTimeoutAt = searchTimeoutCooldown.get(cooldownKey) || 0;
  if (Date.now() - lastTimeoutAt < 12000) {
    return [];
  }
  return await Promise.race<Property[]>([
    (async () => {
      const now = Date.now();
      if (now - lastSearchTime < MIN_SEARCH_INTERVAL) {
        await new Promise(resolve => setTimeout(resolve, MIN_SEARCH_INTERVAL - (now - lastSearchTime)));
      }
      lastSearchTime = Date.now();

      const isAddressSearch = !skipAddressDetection && /^\d+\s+/.test(userInput.trim()) && 
                             (userInput.includes(',') || 
                              /[A-Z]{2}/.test(userInput) || 
                              /\b(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(userInput));
      if (isAddressSearch) {
        return await searchSoldPropertiesByAddress(userInput, searchParams, 0, searchSoldProperties);
      }

      const location = await getLocationFromAutoComplete(userInput);
      const searchParamsWithLocation: SoldSearchParams = {
        location,
        status: 'sold',
        ...searchParams
      };
      const searchCacheKey = generateCacheKey(searchParamsWithLocation, 'sold');
      const cachedSearch = propertyCache.get(searchCacheKey);
      if (cachedSearch) {
        return cachedSearch;
      }
      const basicListings = await searchSoldPropertiesWithLocation(searchParamsWithLocation);
      if (basicListings.length === 0) {
        return [];
      }
      const properties: Property[] = basicListings.map(createPropertyFromListing);
      if (onProgress) {
        onProgress(properties, properties.length);
      }
      cacheSoldProperties(properties, userInput.trim(), undefined, searchParamsWithLocation);
      if (onProgress) {
        onProgress(properties, properties.length);
      }
      return properties;
    })(),
    new Promise<Property[]>(resolve => setTimeout(() => {
      try {
        searchTimeoutCooldown.set(cooldownKey, Date.now());
      } catch {}
      resolve([]);
    }, SEARCH_TIMEOUT_MS))
  ]);
};

/**
 * Fetch property details on-demand when user clicks on a property
 */
export const getPropertyDetails = async (property: Property): Promise<Property | null> => {
  try {
    if (!property.source_id) {
      return null;
    }

    // Check if we already have detailed data to avoid duplicate calls
    if (hasDetailedData(property) && property.description && property.description.trim() !== '') {
      return property;
    }

    // Fetch detailed property data
    const detailsUrl = `/api/properties/details?zpid=${property.source_id}`;
    
    const detailsData = await makeApiCall<any>(detailsUrl);
    const enrichedData = detailsData.data || detailsData;

    // Create a hybrid property that combines search data with details API data
    const hybridProperty = createHybridProperty(property, enrichedData);
    
    if (hybridProperty) {
      // Cache the enriched property data to the database
      try {
        await cacheSoldProperties([hybridProperty]);
      } catch (error) {
        // Failed to cache enriched property data
      }
      
      return hybridProperty;
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if a property already has detailed/enriched data
 * IMPORTANT: Always require a description to be considered "detailed"
 */
export const hasDetailedData = (property: Property): boolean => {
  // CRITICAL: Always require a description for any property to be considered "detailed"
  if (!property.description || property.description.trim() === '') {
    return false;
  }
  
  // For sold properties, we need different criteria since price = 0 and description might be filtered
  if (property.status === 'sold') {
    const hasSoldPropertyData = !!(
      property.details?.bedrooms && property.details.bedrooms > 0 &&
      property.details?.bathrooms && property.details.bathrooms > 0 &&
      property.details?.squareFeet && property.details.squareFeet > 0 &&
      property.photos && property.photos.length > 0 &&
      // Check for sold property metadata
      ((property as any).soldPropertyMetadata?.soldDate ||
       (property as any).soldPropertyMetadata?.soldPrice ||
       (property as any).soldPropertyMetadata?.priceHistory ||
       (property as any).soldPropertyMetadata?.homeDetails)
    );
    
    return hasSoldPropertyData;
  }
  
  // For non-sold properties, use the original comprehensive check
  const hasComprehensiveAddressData = !!(
    property.price && property.price > 0 &&
    property.details?.bedrooms && property.details.bedrooms > 0 &&
    property.details?.bathrooms && property.details.bathrooms > 0 &&
    property.details?.squareFeet && property.details.squareFeet > 0 &&
    property.photos && property.photos.length > 0
  );
  
  // Check if we have detailed sold property metadata
  const hasDetailedSoldMetadata = !!(
    (property as any).soldPropertyMetadata?.soldDate ||
    (property as any).soldPropertyMetadata?.soldPrice ||
    (property as any).soldPropertyMetadata?.priceHistory ||
    (property as any).soldPropertyMetadata?.homeDetails
  );
  
  // Return true if we have either comprehensive address data OR detailed sold metadata
  return hasComprehensiveAddressData || hasDetailedSoldMetadata;
};

/**
 * Get the best display price for a sold property
 */
export const getDisplayPrice = (property: Property): string => {
  // Check for sold price first
  if ((property as any).soldInfo?.price) {
    return `$${(property as any).soldInfo.price.toLocaleString()}`;
  }
  
  // Check sold property metadata for sold price
  const soldMetadata = (property as any).soldPropertyMetadata;
  if (soldMetadata?.soldPrice && soldMetadata.soldPrice > 0) {
    return `$${soldMetadata.soldPrice.toLocaleString()}`;
  }
  
  // Use property.price if it's a valid number
  if (typeof property.price === 'number' && !isNaN(property.price) && property.price > 0) {
    return `$${property.price.toLocaleString()}`;
  }
  
  // Handle price object structure
  if (typeof property.price === 'object' && property.price !== null && 'value' in (property.price as any)) {
    const priceValue = (property.price as any).value;
    if (typeof priceValue === 'number' && !isNaN(priceValue) && priceValue > 0) {
      return `$${priceValue.toLocaleString()}`;
    }
  }
  
  return '$-Unavailable';
};

/**
 * Get the sold date for a property
 */
export const getSoldDate = (property: Property): string | null => {
  if ((property as any).soldInfo?.date) {
    return (property as any).soldInfo.date;
  }
  
  const soldMetadata = (property as any).soldPropertyMetadata;
  if (soldMetadata?.soldDate) {
    return soldMetadata.soldDate;
  }
  
  return null;
};

/**
 * Get the sold price for a property
 */
export const getSoldPrice = (property: Property): number | null => {
  if ((property as any).soldInfo?.price) {
    return (property as any).soldInfo.price;
  }
  
  const soldMetadata = (property as any).soldPropertyMetadata;
  if (soldMetadata?.soldPrice && soldMetadata.soldPrice > 0) {
    return soldMetadata.soldPrice;
  }
  
  return null;
};

/**
 * Search sold properties by location parameters
 */
export const searchSoldPropertiesByLocation = debounceSearch(async (params: SearchParams): Promise<Property[]> => {
  try {
    const { city, state, zipCode, sort, ...otherParams } = params;
    
    let location = '';
    if (city && state) {
      location = `${city}, ${state}`;
    } else if (city) {
      location = city;
    } else if (state) {
      location = state;
    } else if (zipCode) {
      location = zipCode;
    } else {
      throw new Error('At least one location parameter is required');
    }
    
    let zillowSort: SoldSearchParams['sort'] = 'relevance';
    if (sort === 'relevant') zillowSort = 'relevance';
    else if (sort === 'price_asc') zillowSort = 'price';
    else if (sort === 'price_desc') zillowSort = 'price';
    
    const searchParams: SoldSearchParams = {
      location,
      status: 'sold',
      sort: zillowSort,
      sortType: sort === 'price_asc' ? 'desc' : 'desc',
      ...otherParams
    };
    
    return await searchSoldProperties(location, searchParams);
  } catch (error) {
    throw error;
  }
}, 200); 